/* The link to NextPlan: whether a ticket the assistant raises is handed on to it.
 *
 * A switch, and deliberately nothing else. Where the tickets go — the address, the secret,
 * the project — is in the assistant's own `.env`, set once when the two systems were
 * introduced to each other and not touched again; neither a form for it nor a read-out of it
 * belongs on a card whose whole job is one decision. What changes is whether delivery is
 * happening, so that is what this panel is.
 *
 * The switch is its own connection test: it will not go on over a helpdesk that cannot be
 * reached. There was a Test button next to it, which was the same request behind a second
 * click — and the only moment the answer matters is the moment somebody switches it on.
 *
 * It sits on the Integrations page because that is where the other connection between these
 * systems already is. The Tickets panel says whether tickets arrive; this decides whether
 * they are sent.
 */

import { useState } from 'react';
import { Switch } from '@nextleap-al/admin-ui';

import { detailOf, write } from '../api/client';
import type { TicketDelivery } from '../api/types';
import { Async, PanelCard, ResultLine } from '../components/Panel';
import { Signal, Signals } from '../components/Signal';
import { useApi } from '../hooks/useApi';
import { shortDate } from '../lib/format';
import { useApp } from '../state/app';

/**
 * Only where something is wrong with the destination. Where it is configured and readable,
 * this card says nothing about it at all: the address lives in a file, changes about once,
 * and printing it here only invited the reader to look for the field that edits it.
 */
function Destination({ item }: { item: TicketDelivery }) {
  if (!item.destination_known) {
    return (
      <p className="text-sm text-[var(--text-secondary)]">
        The address is in the assistant's <code>.env</code>, which this service cannot read —
        the switch works, but nothing here can say where they go.
      </p>
    );
  }
  if (!item.url) {
    return (
      <p className="text-sm text-[var(--text-secondary)]">
        Nothing is set to receive them: the assistant's <code>.env</code> has no{' '}
        <code>TICKET_WEBHOOK_URL</code>, so nothing is sent whatever this switch says.
      </p>
    );
  }
  return null;
}

function DeliveryCard({ item }: { item: TicketDelivery }) {
  const { reload } = useApp();
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  /**
   * The switch is also the check. Turning it on tries the helpdesk first and the server
   * refuses if it cannot be reached, which puts the switch back where it was with the reason
   * on the line below — a switch reading "On" over a connection that does not work is the
   * one thing this card must never show. Turning it off is never refused.
   */
  async function setEnabled(on: boolean) {
    const { ok, data } = await write('/api/ticket-delivery', { enabled: on ? 'true' : 'false' });
    if (!ok) {
      setResult({ ok: false, text: detailOf(data, 'That did not work.') });
      // Rejecting puts the switch back: `Switch` toggles optimistically and reverts on a
      // rejected promise, which is the only honest thing to show when the server refused.
      throw new Error('refused');
    }
    setResult({
      ok: true,
      text: on ? 'Sending. The next ticket filed goes to NextPlan.' : 'Stopped. Tickets stay here.',
    });
    reload();
  }

  return (
    <PanelCard
      title="Ticket delivery"
      hint={
        item.enabled
          ? 'Tickets go to NextPlan, and whoever filed one is given its reference.'
          : 'Tickets stay here — still written and still shown to whoever filed one, just not sent on.'
      }
      /* The switch itself, not a button behind a confirmation. Both directions are one click
         to undo and neither touches anything already filed, and a switch that opens a dialog
         is no longer a switch. What being off actually means is in the hint beside it, and
         said again on the line the toggle writes. */
      action={
        <Switch checked={item.enabled} onChange={setEnabled} label={item.enabled ? 'On' : 'Off'} />
      }
    >
      <div className="space-y-4">
        <Signals>
          {item.enabled ? (
            <Signal tone="good">sending</Signal>
          ) : (
            <Signal tone="warning">not sending</Signal>
          )}
          {item.checked_at &&
            (item.check_ok ? (
              <Signal tone="good">checked {shortDate(item.checked_at)}</Signal>
            ) : (
              <Signal tone="critical">check failed {shortDate(item.checked_at)}</Signal>
            ))}
          {item.untouched && <Signal>never switched</Signal>}
        </Signals>

        <Destination item={item} />

        {result && <ResultLine ok={result.ok}>{result.text}</ResultLine>}
      </div>
    </PanelCard>
  );
}

export function TicketDeliveryPanel() {
  const query = useApi<{ item: TicketDelivery }>('/api/ticket-delivery');
  // `page`, not `card`: the card's own heading says whether delivery is on, so there is no
  // frame worth drawing around a body that is still loading.
  return (
    <Async query={query} shape="page">
      {({ item }) => <DeliveryCard item={item} />}
    </Async>
  );
}
